import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { DOWNLOAD_PAGE_PATH } from "@/config/download";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme, type ThemeMode } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { sendEmailVerifyCode, registerByEmail, loginByEmail, loginByThirdWithAccessToken } from "@/api/auth";

const LANGUAGES = [
  { code: "zh-CN", label: "简体中文" },
  { code: "zh-TW", label: "繁體中文" },
  { code: "en", label: "English" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
];

const THEME_OPTIONS: { value: ThemeMode; label: string; icon: string }[] = [
  { value: "light", label: "浅色", icon: "ri-sun-line" },
  { value: "dark", label: "深色", icon: "ri-moon-line" },
  { value: "system", label: "跟随系统", icon: "ri-computer-line" },
];

type AuthTab = "login" | "register";
type LoginMethod = "email" | "phone";
type LoginInputMode = "password" | "code";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  const { login } = useAuth();
  const { t, i18n } = useTranslation("home");
  const { theme, effectiveTheme, setTheme } = useTheme();
  const isLight = effectiveTheme === "light";

  const [activeTab, setActiveTab] = useState<AuthTab>("login");
  const [loginMethod, setLoginMethod] = useState<LoginMethod>("email");
  const [loginInputMode, setLoginInputMode] = useState<LoginInputMode>("password");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerPasswordConfirm, setRegisterPasswordConfirm] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterPasswordConfirm, setShowRegisterPasswordConfirm] = useState(false);

  const [langOpen, setLangOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(i18n.language || "zh-CN");
  const langRef = useRef<HTMLDivElement>(null);
  const themeRef = useRef<HTMLDivElement>(null);

  const textColor = isLight ? "text-[#1c1c1e]" : "text-white";
  const textMuted = isLight ? "text-[#6e6e73]" : "text-silver-400";
  const textMutedHover = isLight ? "hover:text-[#1c1c1e]" : "hover:text-white";
  const borderColor = isLight ? "border-black/10" : "border-white/10";
  const borderHover = isLight
    ? "hover:border-black/25 hover:bg-black/5"
    : "hover:border-white/30 hover:bg-white/5";
  const iconColor = isLight ? "text-[#6e6e73]" : "text-white/60";
  const inputBg = isLight ? "bg-white" : "bg-white/5";
  const inputBorder = isLight ? "border-[#e5e5ea]" : "border-white/10";
  const inputText = isLight ? "text-[#1c1c1e]" : "text-white";
  const inputPlaceholder = isLight ? "placeholder:text-[#c5c5c7]" : "placeholder:text-silver-600";
  const pageBg = isLight ? "bg-[#f5f5f7]" : "bg-cosmic-950";

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
      if (themeRef.current && !themeRef.current.contains(e.target as Node)) {
        setThemeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    setSelectedLang(i18n.language);
  }, [i18n.language]);

  const handleLangChange = (code: string) => {
    i18n.changeLanguage(code);
    setSelectedLang(code);
    setLangOpen(false);
  };

  const handleThemeChange = (mode: ThemeMode) => {
    setTheme(mode);
    setThemeOpen(false);
  };

  const activeAccount = loginMethod === "email" ? email.trim() : phone.trim();
  const activeAccountLabel = loginMethod === "email" ? "邮箱地址" : "手机号";

  const sendCode = async () => {
    if (!activeAccount) {
      setError(`请输入${activeAccountLabel}`);
      return;
    }
    if (loginMethod === "email") {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(activeAccount)) {
        setError("请输入正确的邮箱地址");
        return;
      }
      try {
        setError("");
        await sendEmailVerifyCode(activeAccount);
        setCountdown(60);
      } catch (e) {
        setError((e as Error)?.message || "验证码发送失败，请稍后重试");
      }
      return;
    }
    setError("");
    setCountdown(60);
  };

  const clearAuthTransientFields = () => {
    setCode("");
    setPassword("");
    setRegisterPassword("");
    setRegisterPasswordConfirm("");
    setError("");
    setCountdown(0);
  };

  const handleSubmit = async () => {
    if (!activeAccount) {
      setError(`请输入${activeAccountLabel}`);
      return;
    }
    if (activeTab === "login") {
      if (loginInputMode === "password" && password.length < 6) {
        setError("请输入至少 6 位密码");
        return;
      }
      if (loginInputMode === "code" && !code.trim()) {
        setError("请输入验证码");
        return;
      }
    } else {
      if (!code.trim()) {
        setError("请输入验证码");
        return;
      }
      if (registerPassword.length < 6) {
        setError("密码长度不能少于 6 位");
        return;
      }
      if (registerPassword !== registerPasswordConfirm) {
        setError("两次输入的密码不一致");
        return;
      }
      if (!agreed) {
        setError("请先同意用户协议和隐私政策");
        return;
      }
    }
    if (loginMethod === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(activeAccount)) {
      setError("请输入正确的邮箱地址");
      return;
    }

    setError("");
    setLoading(true);

    // 邮箱模式：走真实注册/登录接口
    if (loginMethod === "email") {
      try {
        const accountName = activeAccount.split("@")[0] || "命理乾坤用户";
        if (activeTab === "register") {
          const accessToken = await registerByEmail(activeAccount, registerPassword, code.trim());
          const token = await loginByThirdWithAccessToken(accessToken);
          const target = from || "/guide";
          login({ name: accountName, email: activeAccount }, token);
          navigate(target);
        } else {
          const accessToken = await loginByEmail(activeAccount, password);
          const token = await loginByThirdWithAccessToken(accessToken);
          const target = from || "/";
          login({ name: accountName, email: activeAccount }, token);
          navigate(target);
        }
        return;
      } catch (e) {
        setError((e as Error)?.message || "操作失败，请稍后重试");
        setLoading(false);
        return;
      }
    }

    // 手机模式：暂未接真实接口，保持原占位行为
    setTimeout(() => {
      setLoading(false);
      const accountName = `用户${phone.slice(-4) || ""}`;
      login({
        name: accountName || "命理乾坤用户",
        email: `${phone || "phone"}@phone.local`,
      });
      navigate(activeTab === "register" ? "/guide" : "/");
    }, 800);
  };

  const inputShellClass = `w-full rounded-full border text-sm outline-none transition-colors ${inputBg} ${inputBorder} ${inputText} ${inputPlaceholder} focus:border-[#4a4a4c]`;
  const tabOptions: { key: AuthTab; label: string }[] = [
    { key: "login", label: "登录" },
    { key: "register", label: "注册" },
  ];

  return (
    <div className={`min-h-screen w-full ${pageBg} flex flex-col`}>
      {/* Top Bar */}
      <header className="w-full px-6 sm:px-10 lg:px-16 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className={`flex items-center gap-3 hover:opacity-80 transition-opacity ${textColor}`}
        >
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
              isLight ? "bg-[#4a4a4c]" : "bg-gold-500/20"
            }`}
          >
            <img
              src="/images/app-icon-512.webp"
              alt="命理乾坤"
              className="w-7 h-7 object-contain"
            />
          </div>
          <div className="flex flex-col">
            <span className="text-lg font-bold tracking-wider">命理乾坤</span>
            <span className={`text-xs font-medium tracking-wider mt-0.5 ${isLight ? 'text-[#8a6a3e]' : 'text-gold-400/80'}`}>
              "洞悉命理，掌控乾坤"
            </span>
          </div>
        </Link>

        {/* Right Controls */}
        <div className="flex items-center gap-3">
          {/* Theme Switcher */}
          <div className="relative" ref={themeRef}>
            <button
              onClick={() => setThemeOpen(!themeOpen)}
              className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full border transition-all duration-200 cursor-pointer ${borderColor} ${borderHover}`}
              aria-label="切换主题"
            >
              <i
                className={`${
                  effectiveTheme === "light" ? "ri-sun-line" : "ri-moon-line"
                } ${iconColor} text-base md:text-lg`}
              />
            </button>
            <AnimatePresence>
              {themeOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.96 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 top-full mt-2 w-44 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-[100] overflow-hidden"
                >
                  {THEME_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handleThemeChange(opt.value)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150 cursor-pointer ${
                        theme === opt.value
                          ? "text-[#1c1c1e] font-semibold bg-[#f0f0f5]"
                          : "text-[#6e6e73] hover:text-[#1c1c1e] hover:bg-[#f0f0f5]"
                      }`}
                    >
                      <i className={`${opt.icon} text-base`} />
                      <span className="flex-1 text-left">{opt.label}</span>
                      {theme === opt.value && (
                        <i className="ri-check-line text-[#1c1c1e] text-sm" />
                      )}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Language Switcher */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full border transition-all duration-200 cursor-pointer ${borderColor} ${borderHover}`}
              aria-label="切换语言"
            >
              <i className={`ri-global-line ${iconColor} text-base md:text-lg`} />
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.96 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 top-full mt-2 w-44 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-[100] overflow-hidden"
                >
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLangChange(lang.code)}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 cursor-pointer ${
                        selectedLang === lang.code
                          ? "text-[#1c1c1e] font-semibold bg-[#f0f0f5]"
                          : "text-[#6e6e73] hover:text-[#1c1c1e] hover:bg-[#f0f0f5]"
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Download Button */}
          <Link
            to={DOWNLOAD_PAGE_PATH}
            className={`hidden sm:flex items-center gap-2 px-5 py-2 rounded-full border text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
              isLight
                ? "border-[#2d2d2d]/20 text-[#1c1c1e] hover:bg-[#f0f0f5]"
                : "border-gold-500/40 text-gold-400 hover:bg-gold-500/10"
            }`}
          >
            {t("nav.download_now")}
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row items-center justify-center px-6 sm:px-10 lg:px-16 py-8 lg:py-0 gap-8 lg:gap-16">
        {/* Left: Login Form */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm"
        >
          <h1 className={`text-2xl font-bold mb-2 ${textColor}`}>
            {activeTab === "login" ? "欢迎来到命理乾坤" : "开启命理乾坤"}
          </h1>
          <p className={`text-sm mb-8 ${textMuted}`}>
            {activeTab === "login" ? "\"洞悉命理，掌控乾坤\"" : "创建账号后，继续完善你的命主档案。"}
          </p>

          <div className="space-y-4 mb-5">
            <div className="relative">
              <i className={`ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-lg ${iconColor}`} />
              <input
                type="email"
                placeholder="输入你的邮箱地址"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                className={`${inputShellClass} pl-12 pr-4 py-3.5`}
              />
            </div>

            {activeTab === "login" && loginInputMode === "password" && (
              <div className="relative">
                <i className={`ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-lg ${iconColor}`} />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="密码"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError("");
                  }}
                  className={`${inputShellClass} pl-12 pr-12 py-3.5`}
                />
                <button
                  onClick={() => setShowPassword((v) => !v)}
                  className={`absolute right-4 top-1/2 -translate-y-1/2 ${iconColor}`}
                  type="button"
                >
                  <i className={`${showPassword ? "ri-eye-line" : "ri-eye-off-line"} text-lg`} />
                </button>
              </div>
            )}

            {(activeTab === "register" || loginInputMode === "code") && (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <i className={`ri-shield-keyhole-line absolute left-4 top-1/2 -translate-y-1/2 text-lg ${iconColor}`} />
                  <input
                    type="text"
                    placeholder={loginMethod === "email" ? "输入邮箱中的验证码" : "输入短信验证码"}
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value);
                      setError("");
                    }}
                    className={`${inputShellClass} pl-12 pr-4 py-3.5`}
                  />
                </div>
                <button
                  onClick={sendCode}
                  disabled={countdown > 0}
                  className={`shrink-0 px-4 py-3.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    countdown > 0
                      ? isLight
                        ? "bg-[#f0f0f5] text-[#9e9ea2]"
                        : "bg-white/5 text-silver-600"
                      : isLight
                        ? "bg-[#4a4a4c] text-white hover:bg-[#3a3a3c]"
                        : "bg-gold-500 text-cosmic-950 hover:bg-gold-400"
                  }`}
                >
                  {countdown > 0 ? `${countdown}s` : "发送验证码"}
                </button>
              </div>
            )}

            {activeTab === "login" && (
              <div className="flex items-center justify-between px-1">
                {loginInputMode === "password" ? (
                  <button
                    className={`text-xs ${isLight ? "text-[#6e6e73] hover:text-[#1c1c1e]" : "text-silver-400 hover:text-white"}`}
                    type="button"
                  >
                    忘记密码？
                  </button>
                ) : (
                  <span />
                )}
                <button
                  onClick={() => {
                    setLoginInputMode((mode) => (mode === "password" ? "code" : "password"));
                    setCode("");
                    setPassword("");
                    setError("");
                  }}
                  className={`text-xs font-medium ${isLight ? "text-[#4a4a4c]" : "text-gold-400"}`}
                  type="button"
                  style={loginMethod === "email" ? { display: "none" } : undefined}
                >
                  {loginInputMode === "password" ? "验证码登录" : "使用密码"}
                </button>
              </div>
            )}

            {activeTab === "register" && (
              <>
                <div className="relative">
                  <i className={`ri-lock-password-line absolute left-4 top-1/2 -translate-y-1/2 text-lg ${iconColor}`} />
                  <input
                    type={showRegisterPassword ? "text" : "password"}
                    placeholder="设置密码"
                    value={registerPassword}
                    onChange={(e) => {
                      setRegisterPassword(e.target.value);
                      setError("");
                    }}
                    className={`${inputShellClass} pl-12 pr-12 py-3.5`}
                  />
                  <button
                    onClick={() => setShowRegisterPassword((v) => !v)}
                    className={`absolute right-4 top-1/2 -translate-y-1/2 ${iconColor}`}
                    type="button"
                  >
                    <i className={`${showRegisterPassword ? "ri-eye-line" : "ri-eye-off-line"} text-lg`} />
                  </button>
                </div>

                <div className="relative">
                  <i className={`ri-lock-2-line absolute left-4 top-1/2 -translate-y-1/2 text-lg ${iconColor}`} />
                  <input
                    type={showRegisterPasswordConfirm ? "text" : "password"}
                    placeholder="确认密码"
                    value={registerPasswordConfirm}
                    onChange={(e) => {
                      setRegisterPasswordConfirm(e.target.value);
                      setError("");
                    }}
                    className={`${inputShellClass} pl-12 pr-12 py-3.5`}
                  />
                  <button
                    onClick={() => setShowRegisterPasswordConfirm((v) => !v)}
                    className={`absolute right-4 top-1/2 -translate-y-1/2 ${iconColor}`}
                    type="button"
                  >
                    <i className={`${showRegisterPasswordConfirm ? "ri-eye-line" : "ri-eye-off-line"} text-lg`} />
                  </button>
                </div>

                <label className="flex items-start gap-2 px-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => {
                      setAgreed(e.target.checked);
                      setError("");
                    }}
                    className="mt-0.5"
                  />
                  <span className={`text-xs leading-relaxed ${textMuted}`}>
                    我已阅读并同意
                    <Link to="/terms" className={`mx-1 underline ${textMutedHover}`}>用户协议</Link>
                    和
                    <Link to="/privacy" className={`mx-1 underline ${textMutedHover}`}>隐私政策</Link>
                  </span>
                </label>
              </>
            )}
          </div>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="text-xs text-red-500 mb-4"
              >
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition-colors cursor-pointer mb-4 ${
              isLight
                ? "bg-[#4a4a4c] text-white hover:bg-[#3a3a3c] disabled:opacity-50"
                : "bg-gold-500 text-cosmic-950 hover:bg-gold-400 disabled:opacity-50"
            }`}
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin text-xs" />
                {activeTab === "login" ? "登录中..." : "注册中..."}
              </>
            ) : activeTab === "login" ? (
              "立即登录"
            ) : (
              "立即注册"
            )}
          </button>

          <button
            onClick={() => {
              setActiveTab(activeTab === "login" ? "register" : "login");
              clearAuthTransientFields();
            }}
            className={`w-full text-center text-sm mb-6 ${isLight ? "text-[#6e6e73] hover:text-[#1c1c1e]" : "text-silver-400 hover:text-white"}`}
            type="button"
          >
            {activeTab === "login" ? "还没有账号？立即注册" : "已有账号？立即登录"}
          </button>

          <p className={`text-xs text-center ${textMuted}`}>
            使用即代表你同意我们的{" "}
            <Link to="/terms" className="underline hover:opacity-80">
              服务协议
            </Link>{" "}
            &{" "}
            <Link to="/privacy" className="underline hover:opacity-80">
              隐私政策
            </Link>
          </p>
        </motion.div>

        {/* Right: Brand Visual */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="hidden lg:flex flex-1 max-w-xl items-center justify-center"
        >
          <div className="relative w-full aspect-square max-w-[520px] rounded-3xl overflow-hidden">
            {/* Background Image — switches by theme */}
            <img
              src={
                isLight
                  ? "/images/profile/login-brand-light-01.jpg"
                  : "/images/profile/login-brand-dark-01.jpg"
              }
              alt="命理乾坤"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Gradient Overlays — adapt intensity by theme */}
            <div
              className={`absolute inset-0 bg-gradient-to-t ${
                isLight ? "from-black/60 via-black/10 to-transparent" : "from-black/70 via-black/20 to-black/40"
              }`}
            />
            <div
              className={`absolute inset-0 bg-gradient-to-b ${
                isLight ? "from-black/20 via-transparent to-black/40" : "from-black/30 via-transparent to-black/50"
              }`}
            />

            {/* Content Overlay */}
            <div className="absolute inset-0 flex flex-col items-center justify-end pb-10 px-6 text-center">
              {/* Logo */}
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-4 border-2"
                style={{
                  background: isLight ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.35)",
                  borderColor: isLight ? "rgba(74,74,76,0.35)" : "rgba(232,195,158,0.5)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <img
                  src="/images/app-icon-512.webp"
                  alt="命理乾坤"
                  className="w-10 h-10 object-contain"
                />
              </div>

              {/* Brand Name */}
              <h2
                className="text-3xl font-bold mb-2 tracking-widest"
                style={{
                  fontFamily: "'Noto Serif TC', serif",
                  color: isLight ? "#1c1c1e" : "#ffffff",
                  textShadow: isLight
                    ? "0 1px 12px rgba(255,255,255,0.6)"
                    : "0 2px 20px rgba(0,0,0,0.5)",
                }}
              >
                命理乾坤
              </h2>

              {/* Slogan */}
              <p
                className="text-sm font-medium tracking-wider"
                style={{
                  color: isLight ? "#8a6a3e" : "#E8C39E",
                  fontFamily: "'Noto Serif TC', serif",
                  textShadow: isLight
                    ? "0 1px 8px rgba(255,255,255,0.5)"
                    : "0 1px 10px rgba(0,0,0,0.6)",
                }}
              >
                "洞悉命理，掌控乾坤"
              </p>

              {/* Decorative line */}
              <div
                className="mt-4 w-12 h-px rounded-full"
                style={{ background: isLight ? "rgba(74,74,76,0.4)" : "rgba(232,195,158,0.6)" }}
              />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
